import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import {
  useLazyGetCompanyEmployeeQuery,
  useUpdateCompanyEmployeeMutation,
} from "@diditusa/shared/api/companyApi";
import LoadingIndicator from "@diditusa/shared/web_components/LoadingIndicator/loadingIndicator";
import ImageUploadInput from "../../components/ImageUploadInput/ImageUploadInput";
import { ImageEntity, ImagePurpose } from "@diditusa/shared/types/uploadsTypes";
import { UpdateEmployeeReq } from "@diditusa/shared/types/companyTypes";

const AgentProfileEdit: React.FC = () => {
  const { companyId: companyIdParam, agentId } = useParams();
  const navigate = useNavigate();
  const companyId = parseInt(companyIdParam!);
  const [uploadFileId, setUploadFileId] = useState<number | undefined>();

  const [getEmployeeDetails, { data: employee, isLoading }] =
    useLazyGetCompanyEmployeeQuery();
  const [updateCompanyEmployee, updateCompanyEmployeeState] =
    useUpdateCompanyEmployeeMutation();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    dob: "",
    height: "",
    weight: "",
    licenses: "",
    bio: "",
  });

  useEffect(() => {
    if (companyId && agentId) {
      getEmployeeDetails({ companyId, employeeId: agentId });
    }
  }, [companyId, agentId]);

  useEffect(() => {
    if (updateCompanyEmployeeState.isSuccess) {
      navigate(-1);
    }
  }, [updateCompanyEmployeeState.isSuccess]);

  useEffect(() => {
    if (employee) {
      setFormData({
        firstName: employee.firstName || "",
        lastName: employee.lastName || "",
        email: employee.email || "",
        phoneNumber: employee.phoneNumber || "",
        dob: employee.dob
          ? new Date(employee.dob).toISOString().split("T")[0]
          : "",
        height: employee.height || "",
        weight: employee.weight || "",
        licenses: employee.licenses?.join(", ") || "",
        bio: employee.bio || "",
      });
    }
  }, [employee]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onSave = () => {
    if (!employee || !companyId) {
      return;
    }
    const data: UpdateEmployeeReq = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      dob: new Date(formData.dob),
      heightInches: formData.height ? parseInt(formData.height) : undefined,
      weightLbs: formData.weight ? parseInt(formData.weight) : undefined,
      licenses: formData.licenses.split(","),
      bio: formData.bio,
      fileId: uploadFileId,
    };
    updateCompanyEmployee({ companyId, employeeId: employee.id, data });
  };

  if (isLoading) {
    return <LoadingIndicator isVisible={isLoading} />;
  }

  if (!employee) {
    return <Typography>No employee</Typography>;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      minHeight="100vh"
      bgcolor="white"
      textAlign={"start"}
    >
      <Container maxWidth="md" sx={{ py: 4, flex: 1 }}>
        <Typography variant="h4" fontWeight="bold" mb={4}>
          {`Edit Agent Profile: ${employee.firstName} ${employee.lastName}`}
        </Typography>
        <Box display="flex" mb={4}>
          <ImageUploadInput
            purpose={ImagePurpose.PROFILE}
            entity={ImageEntity.USER}
            companyId={companyId}
            userId={employee.id}
            imageUrl={employee.imageUrl}
            onUploadComplete={(fileId: number) => setUploadFileId(fileId)}
          />
          {/* <Box
            sx={{
              height: '250px',
              width: '250px',
              aspectRatio: '2/3',
              borderRadius: 2,
              backgroundImage:
                'url("https://cdn.usegalileo.ai/sdxl10/d15c5385-a495-4889-b4d6-d148e057c1cd.png")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <Button sx={{ mt: 'auto', height: '30px' }}>Change</Button> */}
        </Box>

        <Grid container spacing={2}>
          {[
            { label: "First Name", name: "firstName" },
            { label: "Last Name", name: "lastName" },
            { label: "Email", name: "email" },
            { label: "Phone Number", name: "phoneNumber" },
            { label: "Date of Birth", name: "dob", type: "date" },
            { label: "Height", name: "height" },
            { label: "Weight", name: "weight" },
            { label: "Licenses", name: "licenses" },
          ].map((field, idx) => (
            <Grid size={{ xs: 12, sm: 6 }} key={idx}>
              <TextField
                label={field.label}
                name={field.name}
                type={field.type || "text"}
                value={formData[field.name as keyof typeof formData]}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          ))}

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Biography"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              multiline
              minRows={4}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid
            size={{ xs: 12 }}
            display="flex"
            justifyContent="flex-end"
            gap={2}
          >
            {updateCompanyEmployeeState.isLoading && (
              <LoadingIndicator
                isVisible={updateCompanyEmployeeState.isLoading}
              />
            )}
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button onClick={onSave} variant="contained" color="primary">
              Save
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AgentProfileEdit;
